import React, { useState, useRef, useEffect } from "react";
import { PDFViewer, PDFDownloadLink } from "@react-pdf/renderer";
import axios from "axios";
import DownlightJobPrint from "./DownlightJobPrint";
import { Plus, Trash2, Edit3, Package, Save, CheckCircle2 } from "lucide-react";
import CommonModal from "../common/CommonModal";
import ProductListing from "../salesInvoice/ProductsModal";
import CreditCustomerList from "../common/CreditCustomerList";
import { useProductSearch } from "../../hooks/useProductSearch";

const DownlightForm = () => {
  // PRINT STATE
  const [showPrint, setShowPrint] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [headerImage, setHeaderImage] = useState("");
  // 1. HEADER STATE: Captures all top-level form data
  const [header, setHeader] = useState({
    orderNo: "ORD-001",
    priority: "Normal",
    orderDate: new Date().toISOString().split("T")[0],
    startDate: "",
    endDate: "",
    customerName: "",
    lpoNo: "",
    lpoDate: "",
    deliveryDesk: "",
    salesman: "",
    preparedBy: "",
    signature: "",
  });

  const [salesPersonList, setSalesPersonList] = useState([]);

  useEffect(() => {
    callSalesPersonListAPI();
  }, []);

  const callSalesPersonListAPI = () => {
    try {
      let company_code_from_local_storage =
        localStorage.getItem("companyCode") || "-";
      let apiPaths_from_local_storage = JSON.parse(
        localStorage.getItem("apiPaths") || "[]",
      );
      const currentSelectedDepartment =
        localStorage.getItem("currentSelectedDepartment") || "-";
      const apiConfig = apiPaths_from_local_storage?.[0]?.API_CONFIG || "";

      if (apiConfig) {
        const apiUrl = `${apiConfig}/api/MasterCount/${company_code_from_local_storage}/SALESPERSONLIST/-/${currentSelectedDepartment}`;
        axios.get(apiUrl).then((res) => {
          setSalesPersonList(res.data || []);
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  // 2. ROWS STATE: Captures the production items
  const [rows, setRows] = useState([
    {
      id: Date.now(),
      stockCode: "",
      systemDesc: "",
      customDesc: "",
      qty: "",
      unit: "PCS",
      watts: "",
      cct: "3000K",
      bodyFinish: "",
      driverBrand: "",
    },
  ]);

  const { products, loading, searchBy, setSearchBy, setSearchQuery } =
    useProductSearch();
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [activeRowIndex, setActiveRowIndex] = useState(null);
  const stockCodeRefs = useRef([]);

  // --- HANDLERS ---

  const handleHeaderChange = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const handleRowChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);
  };

  const addRow = () => {
    setRows([
      ...rows,
      {
        id: Date.now(),
        stockCode: "",
        systemDesc: "",
        customDesc: "",
        qty: "",
        unit: "PCS",
        watts: "",
        cct: "3000K",
        bodyFinish: "",
        driverBrand: "",
      },
    ]);
  };

  const removeRow = (id) => {
    if (rows.length > 1) setRows(rows.filter((row) => row.id !== id));
  };

  const onSelect = (product) => {
    if (activeRowIndex === null || !product) return;
    const updated = [...rows];
    updated[activeRowIndex] = {
      ...updated[activeRowIndex],
      stockCode: product.Code || "",
      systemDesc: product.Description || "",
      unit: product.Unit || "PCS",
      customDesc: product.Description || "",
    };
    setRows(updated);
    setProductModalOpen(false);
  };

  const handleSave = () => {
    const finalSubmission = {
      ...header,
      items: rows,
      meta: {
        itemCount: rows.length,
        exportDate: new Date().toLocaleString(),
        status: "Draft/Saved",
      },
    };
    setPrintData(finalSubmission);
    // Try to get header image from localStorage (like Linear)
    try {
      const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
      setHeaderImage(apiPaths[0]?.REP_HEADER || "");
    } catch {
      setHeaderImage("");
    }
    setShowPrint(true);
    // Optionally keep the alert/console
    console.log(
      "GENERATED PRODUCTION JSON:",
      JSON.stringify(finalSubmission, null, 2),
    );
    alert("Production Job Saved! Print preview opened.");
  };

  const labelCls =
    "text-[10px] font-bold text-slate-500 uppercase mb-0.5 tracking-tight";
  const inputCls =
    "w-full p-1.5 text-xs border border-slate-200 rounded focus:border-orange-500 focus:ring-1 focus:ring-orange-200 outline-none transition-all bg-white";
  const textareaCls =
    "w-full bg-transparent outline-none resize-none py-1 px-1 leading-tight scrollbar-hide focus:bg-white transition-colors";

  return (
    <div className="p-3 lg:p-4 space-y-4 bg-[#f8fafc] min-h-screen font-sans">
      {/* Print Preview Modal */}
      {showPrint && printData && (
        <div className="fixed inset-0 z-[100] bg-black bg-opacity-60 flex items-center justify-center">
          <div className="bg-white rounded shadow-lg p-2 max-w-[95vw] max-h-[95vh] flex flex-col">
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold text-lg">Print Preview</span>
              <button
                onClick={() => setShowPrint(false)}
                className="text-red-500 font-bold px-3 py-1 rounded hover:bg-red-100"
              >
                Close
              </button>
            </div>
            <div className="flex-1 min-h-0 min-w-0">
              <PDFViewer width="1000" height="600" showToolbar>
                <DownlightJobPrint data={printData} headerImage={headerImage} />
              </PDFViewer>
            </div>
            <div className="mt-2">
              <PDFDownloadLink
                document={
                  <DownlightJobPrint
                    data={printData}
                    headerImage={headerImage}
                  />
                }
                fileName={`DownlightJob_${printData.orderNo || "job"}.pdf`}
              >
                {({ loading }) =>
                  loading ? (
                    "Preparing document..."
                  ) : (
                    <button className="bg-orange-600 text-white px-4 py-1 rounded">
                      Download PDF
                    </button>
                  )
                }
              </PDFDownloadLink>
            </div>
          </div>
        </div>
      )}
      {/* 1. HEADER SECTION */}
      <div className="grid grid-cols-12 gap-4 bg-white p-4 rounded-md border border-slate-200 shadow-sm">
        {/* Logistics Group */}
        <div className="col-span-12 lg:col-span-5 grid grid-cols-3 gap-2 border-r pr-4 border-slate-100">
          <div className="flex flex-col">
            <label className={labelCls}>Order No</label>
            <input
              type="text"
              className={inputCls}
              value={header.orderNo}
              onChange={(e) => handleHeaderChange("orderNo", e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className={labelCls}>Priority</label>
            <select
              className={inputCls}
              value={header.priority}
              onChange={(e) => handleHeaderChange("priority", e.target.value)}
            >
              <option>Normal</option>
              <option>Urgent</option>
              <option>High Priority</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className={labelCls}>Order Date</label>
            <input
              type="date"
              className={inputCls}
              value={header.orderDate}
              onChange={(e) => handleHeaderChange("orderDate", e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className={labelCls}>Start Date</label>
            <input
              type="date"
              className={inputCls}
              value={header.startDate}
              onChange={(e) => handleHeaderChange("startDate", e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className={labelCls}>End Date</label>
            <input
              type="date"
              className={inputCls}
              value={header.endDate}
              onChange={(e) => handleHeaderChange("endDate", e.target.value)}
            />
          </div>
        </div>

        {/* Customer Group */}
        <div className="col-span-12 lg:col-span-4 grid grid-cols-2 gap-2 border-r pr-4 border-slate-100">
          <div className="flex flex-col col-span-2">
            <label className={labelCls}>Customer Name</label>
            <input
              type="text"
              className={inputCls}
              placeholder="F2 Search or Type"
              value={header.customerName}
              onKeyDown={(e) => {
                if (e.key === "F2") {
                  e.preventDefault();
                  setCustomerModalOpen(true);
                }
              }}
              onChange={(e) =>
                handleHeaderChange("customerName", e.target.value)
              }
            />
          </div>
          <div className="flex flex-col">
            <label className={labelCls}>LPO No</label>
            <input
              type="text"
              className={inputCls}
              placeholder="LPO Ref"
              value={header.lpoNo}
              onChange={(e) => handleHeaderChange("lpoNo", e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className={labelCls}>LPO Date</label>
            <input
              type="date"
              className={inputCls}
              value={header.lpoDate}
              onChange={(e) => handleHeaderChange("lpoDate", e.target.value)}
            />
          </div>
          <div className="flex flex-col col-span-2">
            <label className={labelCls}>Delivery Desk</label>
            <input
              type="text"
              className={inputCls}
              placeholder="Zone / Desk Location"
              value={header.deliveryDesk}
              onChange={(e) =>
                handleHeaderChange("deliveryDesk", e.target.value)
              }
            />
          </div>
        </div>

        {/* Auth Group */}
        <div className="col-span-12 lg:col-span-3 grid grid-cols-1 gap-2">
          <div className="flex flex-col">
            <label className={labelCls}>Salesman</label>
            <select
              className={inputCls}
              value={header.salesman}
              onChange={(e) => handleHeaderChange("salesman", e.target.value)}
            >
              <option value="">Select Staff</option>
              {salesPersonList.map((sperson, index) => (
                <option key={index} value={sperson.EMP_ID}>
                  {sperson.NAME}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className={labelCls}>Prepared By</label>
            <input
              type="text"
              className={inputCls}
              placeholder="Admin Name"
              value={header.preparedBy}
              onChange={(e) => handleHeaderChange("preparedBy", e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className={labelCls}>PROD HEAD</label>
            <input
              type="text"
              className={inputCls}
              placeholder="PROD HEAD"
              value={header.signature}
              onChange={(e) => handleHeaderChange("signature", e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden">
        {/* Table Toolbar */}
        <div className="px-3 py-2 bg-slate-800 text-white flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Package size={14} className="text-orange-400" />
            <h3 className="text-xs font-bold uppercase tracking-wider">
              Production Job Specifications
            </h3>
          </div>
          <div className="flex gap-2">
            <button
              onClick={addRow}
              className="bg-slate-700 hover:bg-slate-600 text-white text-[10px] font-bold px-3 py-1 rounded flex items-center gap-1 transition-colors border border-slate-600"
            >
              <Plus size={12} /> ADD ITEM
            </button>
            <button
              onClick={handleSave}
              className="bg-orange-600 hover:bg-orange-700 text-white text-[10px] font-bold px-4 py-1 rounded flex items-center gap-1 transition-all shadow-lg active:scale-95"
            >
              <Save size={12} /> SAVE JOB
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[1300px]">
            <thead>
              <tr className="bg-slate-50 text-[10px] text-slate-600 uppercase border-b font-bold">
                <th className="p-2 border-r text-center w-10">S#</th>
                <th className="p-2 border-r w-28">Stock Code</th>
                <th className="p-2 border-r w-72">System Description</th>
                <th className="p-2 border-r w-72 bg-indigo-50/50 text-indigo-700 italic">
                  Production Desc
                </th>
                <th className="p-2 border-r text-center w-16 bg-orange-50/40">
                  Qty
                </th>
                <th className="p-2 border-r text-center w-16 bg-orange-50/40">
                  Unit
                </th>
                <th className="p-2 border-r w-20 bg-orange-50/40">Watts</th>
                <th className="p-2 border-r w-24 bg-orange-50/40">CCT</th>
                <th className="p-2 border-r w-28 bg-orange-50/40">
                  Body Finish
                </th>
                <th className="p-2 w-28 bg-orange-50/40">Driver Brand</th>
                <th className="p-2 text-center w-10"></th>
              </tr>
            </thead>
            <tbody className="text-xs">
              {rows.map((row, i) => (
                <tr
                  key={row.id}
                  className="border-b group hover:bg-orange-50/10 transition-all align-top"
                >
                  <td className="p-2 border-r text-center text-slate-400 font-medium">
                    {i + 1}
                  </td>

                  {/* Stock Code with F2 Search */}
                  <td className="p-2 border-r">
                    <input
                      type="text"
                      placeholder="F2 Search..."
                      className="w-full bg-transparent outline-none font-mono text-indigo-600 font-bold"
                      value={row.stockCode}
                      ref={(el) => (stockCodeRefs.current[i] = el)}
                      onKeyDown={(e) => {
                        if (e.key === "F2") {
                          e.preventDefault();
                          setActiveRowIndex(i);
                          setProductModalOpen(true);
                        }
                      }}
                      onChange={(e) =>
                        handleRowChange(i, "stockCode", e.target.value)
                      }
                    />
                  </td>

                  <td className="p-1.5 border-r bg-slate-50/30">
                    <textarea
                      readOnly
                      rows={2}
                      className={`${textareaCls} text-slate-500 italic cursor-not-allowed `}
                      value={row.systemDesc}
                    />
                  </td>

                  {/* Editable Production Description */}
                  <td className="p-1.5 border-r bg-indigo-50/10">
                    <textarea
                      rows={2}
                      placeholder="Custom notes..."
                      className={`${textareaCls} font-medium text-slate-800`}
                      value={row.customDesc}
                      onChange={(e) =>
                        handleRowChange(i, "customDesc", e.target.value)
                      }
                    />
                  </td>

                  {/* Technical Inputs (Fully Wired) */}
                  <td className="p-2 border-r bg-orange-50/5">
                    <input
                      type="text"
                      className="w-full text-center bg-transparent outline-none font-bold"
                      value={row.qty}
                      onChange={(e) =>
                        handleRowChange(i, "qty", e.target.value)
                      }
                      placeholder="0"
                    />
                  </td>
                  <td className="p-2 border-r bg-orange-50/5">
                    <input
                      type="text"
                      className="w-full text-center bg-transparent outline-none"
                      value={row.unit}
                      onChange={(e) =>
                        handleRowChange(i, "unit", e.target.value)
                      }
                      placeholder="PCS"
                    />
                  </td>
                  <td className="p-2 border-r bg-orange-50/5">
                    <input
                      type="text"
                      className="w-full text-center bg-transparent outline-none"
                      value={row.watts}
                      onChange={(e) =>
                        handleRowChange(i, "watts", e.target.value)
                      }
                      placeholder="12W"
                    />
                  </td>
                  <td className="p-2 border-r bg-orange-50/5">
                    <input
                      type="text"
                      className="w-full bg-transparent outline-none"
                      value={row.cct}
                      onChange={(e) =>
                        handleRowChange(i, "cct", e.target.value)
                      }
                      placeholder="3000K"
                    />
                  </td>
                  <td className="p-2 border-r bg-orange-50/5">
                    <input
                      type="text"
                      className="w-full bg-transparent outline-none"
                      value={row.bodyFinish}
                      onChange={(e) =>
                        handleRowChange(i, "bodyFinish", e.target.value)
                      }
                      placeholder="White"
                    />
                  </td>
                  <td className="p-2 bg-orange-50/5">
                    <input
                      type="text"
                      className="w-full bg-transparent outline-none"
                      value={row.driverBrand}
                      onChange={(e) =>
                        handleRowChange(i, "driverBrand", e.target.value)
                      }
                      placeholder="Tridonic"
                    />
                  </td>

                  <td className="p-2 text-center">
                    <button
                      onClick={() => removeRow(row.id)}
                      className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-all"
                    >
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Product Modal */}
      <CommonModal
        isOpen={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        title="Select Product"
        size="xxl"
      >
        <ProductListing
          modalSuggestions={products}
          loading={loading}
          searchBy={searchBy}
          setSearchBy={setSearchBy}
          onSearchQueryChange={setSearchQuery}
          handleProductSelect={onSelect}
        />
      </CommonModal>

      {/* Customer Modal */}
      <CommonModal
        isOpen={customerModalOpen}
        onClose={() => setCustomerModalOpen(false)}
        title="Select Customer"
        size="xxl"
      >
        <CreditCustomerList
          handleCustomerSelect={(customer) => {
            handleHeaderChange("customerName", customer.DESCRIPTION);
            setCustomerModalOpen(false);
          }}
          showSidebar={true}
          defaultType="Customers"
        />
      </CommonModal>
    </div>
  );
};

export default DownlightForm;
